import { sha256 } from 'js-sha256';

import readingLevels from '../../public/json/reading-levels.json';
import gradeLevels from '../../public/json/grade-levels.json';

export const getSchoolFromUser = (diffitUser) => {
  return diffitUser?.school_records?.[0] || {};
};

/**
 * The school to name when addressing a teacher's own school, or null.
 *
 * Mirrors resolve_org_name in diffit-api's share_with_admin_controller — active org first,
 * then the school picked during onboarding — so a surface can't name the school more vaguely
 * than the email that endpoint sends. `selectedOrg` only ever holds an *active* org, so a
 * teacher whose org lapsed falls through to their school record rather than to nothing.
 */
export const getSchoolNameForUser = (selectedOrg, diffitUser) =>
  selectedOrg?.org?.name || getSchoolFromUser(diffitUser)?.name || null;

export const getStateFromUsersSchool = (diffitUser) => {
  const school = getSchoolFromUser(diffitUser);
  return school?.state || '';
};

export const getLowestSupportedGradeLevel = (diffitUser) => {
  // grade_levels contains a list of ranges, e.g., ["K-2", "3-5"]
  // They aren't necessarily sorted
  // Return the lowest grade level as a number

  if (!diffitUser?.grade_levels || diffitUser.grade_levels.length === 0) {
    return null;
  }

  const gradeLevels = diffitUser.grade_levels.map((gradeLevel) => gradeLevel.grade_level);

  if (gradeLevels.includes('K')) {
    return 0;
  } else if (gradeLevels.includes('1')) {
    return 1;
  } else if (gradeLevels.includes('2')) {
    return 2;
  } else if (gradeLevels.includes('3')) {
    return 3;
  } else if (gradeLevels.includes('4')) {
    return 4;
  } else if (gradeLevels.includes('5')) {
    return 5;
  } else if (gradeLevels.includes('6')) {
    return 6;
  } else if (gradeLevels.includes('7')) {
    return 7;
  } else if (gradeLevels.includes('8')) {
    return 8;
  } else if (gradeLevels.includes('9')) {
    return 9;
  } else if (gradeLevels.includes('10')) {
    return 10;
  } else if (gradeLevels.includes('11')) {
    return 11;
  } else if (gradeLevels.includes('12')) {
    return 12;
  } else {
    return 5;
  }
};

export const getInitialReadingLevel = (
  packetReadingLevelId,
  diffitUser,
  gradeLevel,
  optionContext
) => {
  // Default reading level (5th Grade)
  const defaultReadingLevel = readingLevels.find((level) => level.id === 5);

  // Handle 'packet' context: Use packetReadingLevelId exclusively
  if (optionContext === 'packet') {
    if (packetReadingLevelId !== undefined && packetReadingLevelId !== null) {
      return (
        readingLevels.find((level) => level.id === packetReadingLevelId) || defaultReadingLevel
      );
    }
    // If packet context but no valid ID, maybe return default or handle error? Returning default for now.
    return defaultReadingLevel;
  }

  // Handle 'generate' or other contexts with the specified order of precedence
  // 1. Check cache
  const cachedReadingLevelId = localStorage.getItem('packet-reading-level-id');
  if (cachedReadingLevelId) {
    const cachedLevel = readingLevels.find((level) => level.id === parseInt(cachedReadingLevelId));
    if (cachedLevel) return cachedLevel;
  }

  // 2. Check gradeLevel parameter
  if (gradeLevel) {
    const gradeLevelMatch = readingLevels.find((level) => level.id.toString() === gradeLevel);
    if (gradeLevelMatch) return gradeLevelMatch;
  }

  // 3. Check user's lowest supported grade level
  const lowestUserGradeLevelId = getLowestSupportedGradeLevel(diffitUser);
  if (lowestUserGradeLevelId) {
    const userGradeLevelMatch = readingLevels.find((level) => level.id === lowestUserGradeLevelId);
    if (userGradeLevelMatch) return userGradeLevelMatch;
  }

  // 4. Fallback to default
  return defaultReadingLevel;
};

export const getInitialGradeLevel = (diffitUser) => {
  // first: check the cache
  let cachedGradeLevelId = localStorage.getItem('skills::selectedGradeLevel');
  if (!!cachedGradeLevelId) {
    return gradeLevels[cachedGradeLevelId];
  }

  // second: if there is a user, check to see if the user has a grade level
  const lowestUserGradeLevelId = getLowestSupportedGradeLevel(diffitUser);

  if (lowestUserGradeLevelId) {
    return gradeLevels[lowestUserGradeLevelId];
  }

  // last: if all else fails, return the default grade level (5th Grade)
  return gradeLevels['5'];
};

export const getSubjects = (diffitUser) => {
  const subjects = diffitUser?.subjects?.map((subject) => subject.subject) || [];
  return subjects;
};

export const getRoles = (diffitUser) => {
  const roles = diffitUser?.roles?.map((role) => role.role) || [];
  return roles;
};

export const getClasses = (diffitUser) => {
  const classes = diffitUser?.classes?.map((classObj) => classObj.class_name) || [];
  return classes;
};

export const getGoogleAccount = (user) => {
  if (!user || !user.externalAccounts) {
    return null;
  }
  return user.externalAccounts.find((account) => account.provider === 'google');
};

export const userHasGoogleAccount = (user) => {
  if (!user || !user.externalAccounts) {
    return false;
  }
  let googleAccount = user.externalAccounts.find((account) => account.provider === 'google');
  let hasGoogleAccount = !!googleAccount;
  return hasGoogleAccount;
};

export const userHasMicrosoftAccount = (user) => {
  if (!user || !user.externalAccounts) {
    return false;
  }
  let microsoftAccount = user.externalAccounts.find((account) => account.provider === 'microsoft');
  let hasMicrosoftAccount = !!microsoftAccount;
  return hasMicrosoftAccount;
};

export const constructMailToLinkForUser = ({ user, toAddress = '', subject = '', body = '' }) => {
  if (user && userHasGoogleAccount(user)) {
    // if the user is signed in with google, open a link straight to gmail
    return `https://mail.google.com/mail/?view=cm&fs=1&su=${subject}&body=${body}&to=${toAddress}`;
  } else {
    // otherwise, use the default mail client
    return `mailto:?subject=${subject}&body=${body}&to=${toAddress}`;
  }
};

export const getUserLoginType = (user) => {
  if (userHasGoogleAccount(user)) {
    return 'google';
  } else if (userHasMicrosoftAccount(user)) {
    return 'microsoft';
  } else {
    return 'email';
  }
};

export const isDiffitBot = () => {
  const sessionId = localStorage.getItem('diffitSessionId');
  if (sessionId) {
    const hash = sha256(sessionId);
    if (hash === import.meta.env.VITE_DIFFIT_BOT_HASH) {
      return true;
    }
  }
  return false;
};

export const isPremiumDiffitBot = () => {
  const url = new URL(window.location.href);
  const hasPremiumQueryParam = url.searchParams.get('isPremium') === 'true';
  if (hasPremiumQueryParam) {
    return isDiffitBot();
  }
  return false;
};

export const SEEN_NUX_KEYS = {
  CLOSED_EXPORT_HELPER_TOOLTIP: 'closed_export_helper_tooltip',
  EXPORT_BUTTON_CLICK_COUNT: 'export_button_click_count',
  EXPORTED_PACKET: 'exported_packet',
  EXITED_PACKET_TOUR: 'exited_packet_tour',
  WELCOME_TO_DIFFIT: 'welcome_to_diffit',
  WELCOME_TO_PREMIUM: 'welcome_to_premium',
  PACKET_TOUR: 'packet_tour',
  USER_COMPLETED_ONBOARDING: 'user_completed_onboarding',
  CHAT_TRIAL_EXTENSION_CLAIMED: 'chat_trial_extension_claimed',
  CHAT_SIDEBAR_DISMISSED_AS_FREE_USER: 'chat_sidebar_dismissed_as_free_user',
  WORKSHOP_WELCOME_SEEN: 'workshop_welcome_seen',
  PLUS_TOOLTIP_DISMISSED: 'plus_tooltip_dismissed',
  PROJECT_QUOTA_ANNOUNCEMENT_DISMISSED: 'project_quota_announcement_dismissed_2026',
  SLIDES_THEME_PREFERENCE: 'slides_theme_preference',
  // One key per card in AppSidebar's bottom slot, versioned so a future card
  // there isn't pre-dismissed for teachers who closed the current one. Distinct
  // from WELCOME_TO_DIFFIT, which is the onboarding "is this a new user" flag.
  SIDEBAR_CARD_DISMISSED_WELCOME: 'sidebar_card_dismissed_welcome_2026',
  SIDEBAR_CARD_DISMISSED_WELCOME_CLASSIC: 'sidebar_card_dismissed_welcome_classic_2026',
  // The BTS tip card holds that same slot during the back-to-school window. Its
  // value predates the naming above and is live user data, so it keeps the
  // original string rather than being renamed to match its siblings.
  BTS_CHECKLIST_2026: 'bts_checklist_2026',
  // Prefix, not a whole key: badge acknowledgements are stored per badge type
  // as `badge_seen_<type>` (badge_seen_ambassador, ...). Build them with
  // badgeSeenNuxKey() in utils/badgeNotification.js rather than by hand.
  BADGE_SEEN_PREFIX: 'badge_seen_',
};
